import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Card,
  CardContent,
  Typography,
  Tooltip,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import Loader from "../../../components/loader/Loader";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import ExcelJS from "exceljs";
import { useTableHeight } from "../../../hooks/useTablePagination";

// NOTE: Adjust these selectors to match wherever your categories / sub-categories
// live in the redux store (mirrors how regions/shops were pulled in YearlyReport).
function ProductReport() {
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [category, setCategory] = useState([]); // multi-select category_ids
  const [subCategory, setSubCategory] = useState([]); // multi-select sub_category_ids

  const allCategories = useSelector((state) => state.counter.categoryList || []);
  const allSubCategories = useSelector((state) => state.counter.subCategoryList || []);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const tableHeight = useTableHeight();

  // ── filter sub-categories based on selected categories ─────────────────
  useEffect(() => {
    if (category.length > 0) {
      setFilteredSubCategories(
        allSubCategories.filter((sc) => category.includes(sc.category_id)),
      );
    } else {
      setFilteredSubCategories(allSubCategories);
    }
  }, [category, allSubCategories]);

  // ── load on filter change ───────────────────────────────────────────────
  useEffect(() => {
    loadReport();
  }, [statusFilter, category, subCategory]);

  // ── API call ─────────────────────────────────────────────────────────
  const loadReport = () => {
    setLoader(true);

    const body = {
      status: statusFilter === "ALL" ? undefined : statusFilter,
      category_ids: category.length ? category : undefined,
      sub_category_ids: subCategory.length ? subCategory : undefined,
    };

    axios({
      url: "/reports/productsReport",
      method: "get",
      params: body, // sent as ?status=...&category_ids=...&sub_category_ids=...
      paramsSerializer: { indexes: null }, // category_ids=1&category_ids=2 (no [] or index suffix)
    })
      .then((res) => {
        const payload = res?.data?.data || {};
        const summary = payload.summary || {};
        const arr = Array.isArray(payload.data) ? payload.data : [];

        setFormData(arr);
        setTotalCount(summary.total_products ?? arr.length);
        setActiveCount(summary.active_products ?? 0);
        setInactiveCount(summary.inactive_products ?? 0);
      })
      .catch(() => {
        setFormData([]);
        setTotalCount(0);
        setActiveCount(0);
        setInactiveCount(0);
      })
      .finally(() => setLoader(false));
  };

  // Client-side status filter (active_status: 1 = Active, 0 = Inactive)
  const filteredFormData = formData.filter((row) => {
    if (statusFilter === "ALL") return true;
    const isActive = Number(row.active_status) === 1;
    return statusFilter === "Active" ? isActive : !isActive;
  });

  const formatIndianCurrency = (num) => {
    const n = parseFloat(num) || 0;
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ── Excel export ─────────────────────────────────────────────────────
  const handleCustomDownload = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Product Report");

      const toNumber = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      };

      worksheet.columns = [
        { header: "Product Name", key: "product_name", width: 25 },
        { header: "Category", key: "category_name", width: 18 },
        { header: "Sub Category", key: "sub_category_name", width: 18 },
        { header: "Price (₹)", key: "product_price", width: 15 },
        { header: "Discount Price (₹)", key: "product_discount_price", width: 18 },
        { header: "Display Price (₹)", key: "display_price", width: 18 },
        { header: "Offer (%)", key: "offer", width: 12 },
        { header: "Cities", key: "cities", width: 30 },
        { header: "Status", key: "status", width: 12 },
      ];

      filteredFormData.forEach((m) => {
        const cityNames = (m.region || [])
          .map((r) => r?.itemName?.city?.name)
          .filter(Boolean);

        worksheet.addRow({
          product_name: m.product_name || "-",
          category_name: m.category?.category_name || "-",
          sub_category_name: m.sub_category?.sub_category_name || "-",
          product_price: toNumber(m.product_price),
          product_discount_price: toNumber(m.product_discount_price),
          display_price: toNumber(m.display_price),
          offer: toNumber(m.offer),
          cities: cityNames.length ? cityNames.join(", ") : "-",
          status: Number(m.active_status) === 1 ? "Active" : "Inactive",
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Product_Report.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel error:", err);
      alert("Failed to generate Excel file.");
    }
  };

  // ── MUIDataTable columns (trimmed to fields the API actually returns) ──
  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },
    {
      name: "product_name",
      label: "Product",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {filteredFormData[i]?.product_name || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "category_name",
      label: "Category",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {filteredFormData[i]?.category?.category_name || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "sub_category_name",
      label: "Sub Category",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {filteredFormData[i]?.sub_category?.sub_category_name || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "product_price",
      label: "Price",
      options: {
        customBodyRenderLite: (i) => {
          const price = filteredFormData[i]?.product_price;
          return (
            <Typography variant="body2">
              {price ? `₹${formatIndianCurrency(price)}` : "-"}
            </Typography>
          );
        },
      },
    },
    {
      name: "region_city",
      label: "City",
      options: {
        customBodyRenderLite: (i) => {
          const regions = filteredFormData[i]?.region || [];
          const cityNames = regions
            .map((r) => r?.itemName?.city?.name)
            .filter(Boolean);

          if (cityNames.length === 0) {
            return (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography variant="body2">-</Typography>
              </Box>
            );
          }

          const tooltipContent = (
            <div style={{ padding: "4px", minWidth: 140 }}>
              <div style={{ marginBottom: "6px", fontWeight: 700 }}>
                Cities Available
              </div>
              {cityNames.map((name, idx) => (
                <div key={idx}>{name}</div>
              ))}
            </div>
          );

          return (
            <Tooltip
              title={tooltipContent}
              arrow
              placement="bottom"
              PopperProps={{
                modifiers: [
                  {
                    name: "flip",
                    enabled: true,
                    options: { fallbackPlacements: ["top", "right", "left"] },
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: { boundary: "window" },
                  },
                ],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <LocationCityIcon sx={{ fontSize: 20, color: "#1565c0" }} />
              </Box>
            </Tooltip>
          );
        },
      },
    },
    // {
    //   name: "offer",
    //   label: "Offer",
    //   options: {
    //     customBodyRenderLite: (i) => {
    //       const offer = filteredFormData[i]?.offer;
    //       if (!offer) return <Typography variant="body2">-</Typography>;
    //       return (
    //         <Chip
    //           icon={<LocalOfferOutlinedIcon sx={{ fontSize: 14 }} />}
    //           label={`${offer}%`}
    //           size="small"
    //           sx={{ bgcolor: "#fff3e0", color: "#ed6c02", fontWeight: 600 }}
    //         />
    //       );
    //     },
    //   },
    // },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => {
          const isActive = Number(filteredFormData[i]?.active_status) === 1;
          return (
            <Chip
              icon={
                isActive ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                ) : (
                  <CancelOutlinedIcon sx={{ fontSize: 14 }} />
                )
              }
              label={isActive ? "Active" : "Inactive"}
              size="small"
              sx={{
                bgcolor: isActive ? "#e8f5e9" : "#ffebee",
                color: isActive ? "#2e7d32" : "#c62828",
                fontWeight: 600,
              }}
            />
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: true,
    tableBodyHeight: tableHeight,
    selectableRows: "none",
    rowsPerPage: 12,
    count: filteredFormData.length,
    serverSide: false,
    print: false,
    viewColumns: false,
    download: true,
    onDownload: () => {
      handleCustomDownload();
      return false;
    },
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No records found",
      },
    },
  };

  // ✅ Status chip config
  const statusOptions = [
    { value: "ALL", label: "All", color: "#1565c0", bg: "#e3f2fd" },
    { value: "Active", label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
    { value: "Inactive", label: "Inactive", color: "#c62828", bg: "#ffebee" },
  ];

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <h4 className="text-dark">Product Report</h4>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={statusFilter === "ALL" ? "All Status" : statusFilter}
                color="primary"
                size="small"
              />
              {filteredFormData.length > 0 && (
                <Chip
                  label={`${filteredFormData.length} products`}
                  color="success"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* ── Filters ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Category */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  multiple
                  value={category}
                  label="Category"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.includes("ALL")) {
                      setCategory([]);
                    } else {
                      setCategory(value);
                    }
                    setSubCategory([]); // reset sub-category on category change
                  }}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? "All"
                      : selected
                          .map((id) => allCategories.find((c) => c.id === id)?.category_name)
                          .join(", ")
                  }
                >
                  <MenuItem value="ALL">
                    <em>All</em>
                  </MenuItem>
                  {allCategories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.category_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sub Category */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub Category</InputLabel>
                <Select
                  multiple
                  value={subCategory}
                  label="Sub Category"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.includes("ALL")) {
                      setSubCategory([]);
                    } else {
                      setSubCategory(value);
                    }
                  }}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? "All"
                      : selected
                          .map(
                            (id) =>
                              filteredSubCategories.find((sc) => sc.id === id)
                                ?.sub_category_name,
                          )
                          .join(", ")
                  }
                >
                  <MenuItem value="ALL">
                    <em>All</em>
                  </MenuItem>
                  {filteredSubCategories.map((sc) => (
                    <MenuItem key={sc.id} value={sc.id}>
                      {sc.sub_category_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ✅ Status Filter Chips */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  height: "100%",
                  flexWrap: "wrap",
                }}
              >
                {statusOptions.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => setStatusFilter(opt.value)}
                    sx={{
                      fontWeight: 600,
                      cursor: "pointer",
                      bgcolor: statusFilter === opt.value ? opt.color : opt.bg,
                      color: statusFilter === opt.value ? "#fff" : opt.color,
                      border: `1.5px solid ${opt.color}`,
                      "&:hover": { bgcolor: opt.color, color: "#fff" },
                      transition: "all 0.2s",
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* ── Main Content ── */}
          <Grid container spacing={2}>
            {/* MUIDataTable */}
            <Grid item xs={12} md={12 }>
              <MUIDataTable
                title="Product Report"
                data={filteredFormData}
                columns={columns}
                options={options}
              />
            </Grid>

            
          </Grid>
        </>
      )}
    </>
  );
}

export default ProductReport;